import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LockIcon from '@mui/icons-material/Lock';
import GlowCard from '../../shared/GlowCard';
import ProgressRing from '../../shared/ProgressRing';
import { useColors } from '../../theme/ColorModeContext';
import { SkillTreeNodeData } from './skillTreeData';

interface Stats {
    challenges: number;
    solved: number;
}

interface SkillTreeNodeProps {
    node: SkillTreeNodeData;
    stats?: Stats;
    locked: boolean;
    animateDelay: number;
}

const SkillTreeNode = ({ node, stats, locked, animateDelay }: SkillTreeNodeProps) => {
    const colors = useColors();
    const solvedCount = stats ? stats.solved : 0;
    const totalCount = stats ? stats.challenges : 0;
    const progress = totalCount > 0 ? (solvedCount / totalCount) * 100 : 0;

    return (
        <Box sx={{ width: { xs: '260px', sm: '280px', md: '260px' }, flexShrink: 0 }}>
            <Link
                    to={locked ? '/register' : `/academy/${node.slug}`}
                    style={{ textDecoration: 'none', color: 'inherit' }}
                >
                    <GlowCard animate animateDelay={animateDelay} sx={{ position: 'relative' }}>
                        <Box sx={{ position: 'relative', padding: '14px', paddingBottom: '8px' }}>
                            <img
                                src={`/images/academy/${node.image}`}
                                alt={node.title}
                                style={{
                                    objectFit: 'cover',
                                    width: '100%',
                                    aspectRatio: '1 / 1',
                                    display: 'block',
                                    borderRadius: '8px',
                                    ...(locked ? { filter: 'grayscale(100%) brightness(0.5)' } : {}),
                                }}
                            />
                            {locked && (
                                <Box sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    textAlign: 'center',
                                }}>
                                    <LockIcon sx={{ fontSize: 36, color: colors.text.muted }} />
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ textAlign: 'center', padding: '10px 14px 14px', position: 'relative' }}>
                            <Typography variant="h6" sx={{
                                fontFamily: '"Orbitron", sans-serif',
                                fontWeight: 600,
                                letterSpacing: '0.03em',
                                textTransform: 'uppercase',
                                fontSize: '0.85rem',
                                marginBottom: '8px',
                            }}>
                                {node.title}
                            </Typography>
                            {stats ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                    <ProgressRing value={progress} size={36} strokeWidth={3} showLabel={false} />
                                    <Typography variant="body2" sx={{ color: colors.text.secondary, fontSize: '0.8rem' }}>
                                        {solvedCount} / {totalCount} Solved
                                    </Typography>
                                </Box>
                            ) : node.metaLabel ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minHeight: '36px' }}>
                                    <Box sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: '50%',
                                        border: `4px solid ${colors.border.subtle}`,
                                        boxSizing: 'border-box',
                                    }} />
                                    <Typography variant="body2" sx={{ color: colors.text.secondary, fontSize: '0.8rem' }}>
                                        {node.metaLabel}
                                    </Typography>
                                </Box>
                            ) : (
                                <Typography variant="body2" sx={{ color: colors.text.muted, minHeight: '36px' }}>&nbsp;</Typography>
                            )}
                        </Box>
                        {locked && (
                            <Box sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                padding: '8px',
                                textAlign: 'center',
                                background: 'linear-gradient(transparent, rgba(10, 14, 23, 0.9))',
                                animation: 'subtlePulse 3s ease-in-out infinite',
                            }}>
                                <Typography variant="caption" sx={{
                                    color: colors.text.muted,
                                    fontFamily: '"Orbitron", sans-serif',
                                    fontWeight: 500,
                                }}>
                                    Sign up to unlock
                                </Typography>
                            </Box>
                        )}
                </GlowCard>
            </Link>
        </Box>
    );
};

export default SkillTreeNode;
